import { useEffect, useRef, type ReactElement } from 'react';
import { useSession } from '../auth';
import { setTrackingPage, track } from '../../../../shared-web/track';
import { endPageVisit } from '../../../../shared-web/page-engagement';
import { emberPageSurface } from '../../../../shared/analytics/ember-page-surface.js';
import EmberLockup from '../../../../shared-web/EmberLockup';
import { AddToHomeButton } from '../../../../shared-web/AddToHomeScreen';
import { installKeyboardViewportContract } from './keyboard-viewport';
import { isInAppWebview } from '../../../../shared-web/ua-detect';
import { loadClientConfig } from './client-config';
import { documentTitleFor } from './page-title';
import { consumeGoogleLanding } from '../../../../shared-web/google-signin';
import { lastNavigationMethod, navigate, sanitizeReturnTo, useRoute, type RouteLocation } from './router';
import { TalkSheetProvider } from './talk-sheet';
import { Link } from './components/Link';
import { AccountMenu } from './components/AccountMenu';
import { BlogNav } from './components/BlogNav';
import { PendingDeletionScreen } from './components/PendingDeletionScreen';
import { AccountClosedScreen } from './components/AccountClosedScreen';
import { InboundWaitOverlay } from './components/InboundWaitOverlay';
import { LandingPage } from './pages/Landing';
import { TalkPage } from './pages/Talk';
import { TalkChooserPage } from './pages/TalkChooser';
import { AuthPage } from './pages/Auth';
import { ResultPage } from './pages/Result';
import { MeShell } from './pages/MeShell';
import { NotebookUnlockPage } from './pages/NotebookUnlock';
import { SettingsPage } from './pages/Settings';
import { FeedbackPage } from './pages/Feedback';
import { LegalUpdatePage } from './pages/LegalUpdate';
import { AgeCheckPage } from './pages/AgeCheckPage';
import { OwnerEmberDetailPage } from './pages/OwnerEmberDetail';
import { LetterPage } from './pages/LetterPage';
import { EmberTranscriptPage } from './pages/EmberTranscript';
import { PersonPage } from './pages/PersonPage';
import { HandoffPage } from './pages/HandoffPage';
import { LetterHandoffPage } from './pages/LetterHandoffPage';
import { ReadPage } from './pages/ReadPage';
import { HowItWentPage } from './pages/HowItWentPage';
import { QuestionPage } from './pages/QuestionPage';
import { RoomPage } from './pages/RoomPage';
import { FriendsPage } from './pages/friends/FriendsPage';
import { FriendSetupPage } from './pages/friends/FriendSetupPage';
import { FriendIntroPage } from './pages/friends/FriendIntroPage';
import { FriendCheckinPage } from './pages/friends/FriendCheckinPage';
import { TopicNewPage } from './pages/friends/TopicNewPage';
import { TopicRoomPage } from './pages/friends/TopicRoomPage';
import { GroupsPage } from './pages/groups/GroupsPage';
import { GroupPage } from './pages/groups/GroupPage';
import { GroupNewPage } from './pages/groups/GroupNewPage';
import { GroupPostPage } from './pages/groups/GroupPostPage';
import { ChatsPage, ChatsPane } from './pages/Chats';
import { ChatBanner } from './components/ChatBanner';
import { useNotifyOpen } from './desktop-notify';
import { ChatFrameProvider, ChatRoomFrame, useWideWindow } from './chat-frame';
import { bannerWhereOf, chatHereOf, hasChatList } from './chats';
import { useChatsLive, useChatsSnapshot } from './chats-store';
import { useLive } from './live';
import { PlacePage } from './pages/PlacePage';
import { QuizTalkHandoff } from './pages/QuizTalkHandoff';
import { YardTalkHandoff } from './pages/YardTalkHandoff';
import { GasMeUpTalkHandoff } from './pages/GasMeUpTalkHandoff';
import { CheckTalkHandoff } from './pages/CheckTalkHandoff';
import './tokens.css';
import './EmberApp.css';
import { PageFailure } from './components/PageFailure';
import { readBootstrap } from '../../types/bootstrap';
import type { EmberSsrData } from './api';
import { useBrowserLayoutEffect } from './use-browser-layout-effect';


/**
 * Ember v1.2 SPA shell (新设计文档 §1.5, §2): the public nav (Logo / Search /
 * Profile+badge) over a hand-rolled history router. Server bootstrap
 * `route:'ember'` mounts this for every product path; the router reads
 * window.location from there on.
 */
export function EmberApp({
  initialSsrData,
}: {
  /** The exact public payload used for the server render. */
  initialSsrData?: EmberSsrData | null;
} = {}): ReactElement {
  const loc = useRoute();
  const hideTopNav = loc.route.name === 'landing'
    || loc.route.name === 'retired'
    || loc.route.name === 'talk'
    || loc.route.name === 'place'
    || loc.route.name === 'auth'
    || loc.route.name === 'result'
    || loc.route.name === 'settings'
    || loc.route.name === 'settings-space'
    || loc.route.name === 'feedback'
    || loc.route.name === 'legal-update'
    || loc.route.name === 'age'
    || loc.route.name === 'me'
    || loc.route.name === 'me-embers'
    || loc.route.name === 'me-letters'
    || loc.route.name === 'me-letter-detail'
    || loc.route.name === 'me-ember-detail'
    || loc.route.name === 'me-ember-transcript'
    || loc.route.name === 'person'
    || loc.route.name === 'read'
    // The room's page wears the talk page's header (2026-10-06): the thread
    // and the room's other states under one header.
    || loc.route.name === 'room'
    || loc.route.name === 'friends' || loc.route.name === 'friends-new' || loc.route.name === 'friend-intro'
    || loc.route.name === 'friend-checkin' || loc.route.name === 'topic-new' || loc.route.name === 'topic-room'
    || loc.route.name === 'groups' || loc.route.name === 'group' || loc.route.name === 'group-new' || loc.route.name === 'group-post'
    || loc.route.name === 'chats'
    || loc.route.name === 'how-it-went'
    // 2026-09-06 走查: the dead-URL page carries its own Back/Home row (PageFailure);
    // the site nav stacked a second header over it — the only page still wearing it.
    || loc.route.name === 'not-found';

  // Ember pages are desktop-capable; lift the 430px body cap (tokens.css)
  // for exactly as long as this app is mounted.
  useBrowserLayoutEffect(() => {
    document.body.classList.add('ember-body');
    // An Android webview sits below its host's bar but reports the status bar
    // as the top safe area; --eh-safe-top (EmberApp.css) is nought there.
    const androidWebview = /Android/i.test(window.navigator.userAgent) && isInAppWebview();
    document.body.classList.toggle('eh-android-webview', androidWebview);
    return () => document.body.classList.remove('ember-body', 'eh-android-webview');
  }, []);

  // One keyboard/visual-viewport contract for every Ember input surface —
  // self-calibrating across Safari (overlay keyboard), iOS standalone PWA
  // (layout viewport resizes, and can stick shrunk), and Android Chrome.
  // All mechanics and the hidden debug HUD live in keyboard-viewport.ts.
  useBrowserLayoutEffect(() => installKeyboardViewportContract(), []);

  // '/me' is a shell alias — land on the default tab, Your Embers (§4.10,
  // reordered 2026-09-01 拍板): the page is the person's own corner, so what
  // they told leads; the questions they follow are one tab over.
  // '/settings/space' retired the same day: the symbol + line are edited on
  // the /me header itself, so old links land where the editing now lives.
  useEffect(() => {
    if (loc.route.name === 'me' || loc.route.name === 'settings-space') {
      navigate('/me/embers', { replace: true });
    }
  }, [loc.route.name]);

  // Google 授权码流的落地 hash(#ember-google=new|linked|conflict):新号点亮
  // 「绑个手机号」的一次性红点;读一次即抹。Settings 自己也读(它要 toast
  // link 的结果)——谁先挂载谁消费,另一个拿到空串。
  useEffect(() => { consumeGoogleLanding(); }, []);
  // The admin-set client constants (2026-10-19 Riley, client-config.ts): read once as the app starts.
  useEffect(() => { void loadClientConfig(); }, []);
  // The tab's title follows the page (page-title.ts): the document's own on its first address — a place's names the
  // place — and on any other, what a full load of it would carry.
  const bootTitle = useRef<{ path: string; title: string } | null>(null);
  useEffect(() => {
    if (!bootTitle.current) { bootTitle.current = { path: loc.path, title: document.title }; return; }
    document.title = loc.path === bootTitle.current.path ? bootTitle.current.title : documentTitleFor(loc.path);
  }, [loc.path]);

  // The chat surfaces (2026-10-20): the Chats list, the conversation with Ember
  // (a room's thread in it), a room's own page. While one is on screen the list
  // is kept live (chats-store.ts) for the back control's count, the banner and
  // the list itself, and the surfaces wear the skin of the hour on the person's
  // clock. A wide window stands the list beside the conversation (every
  // signed-in person's since 2026-10-21 Riley; Ember's row alone with no room).
  const { status: sessionStatus, session } = useSession();
  const chatSurface = loc.route.name === 'chats' || loc.route.name === 'room'
    || (loc.route.name === 'talk' && loc.search.get('mode') === 'chat');
  const signedIn = sessionStatus === 'authenticated';
  // The live channel (2026-10-21, live.ts): open while signed in; it says when the list, the letters or an Ember changed.
  // Signed in only (2026-10-27 prod audit, Riley): phase 3 had opened it for guests too, for their conversation's
  // channel, and the token request was writing an orphan visitor row on most first visits (the page's first reads mint
  // a cookie each; the edge now answers a guest 401). A guest's reply streams on the POST, as it always could.
  useLive(signedIn, session?.actor_id || 'user');
  const wide = useWideWindow();
  const chatHere = chatHereOf(loc.path, loc.search);
  const split = chatSurface && wide && hasChatList(sessionStatus);
  // Where the banner may drop (2026-10-26 Riley): over every page but home, the list, a call and sign-in.
  const bannerWhere = bannerWhereOf(loc.route as { name: string; roomId?: string }, loc.search, split);
  // Kept live on every page (2026-10-26): a line that lands while the tab is away is the system's notification, home and
  // the list included
  useChatsLive(signedIn);
  useNotifyOpen(signedIn);
  const { data: chats } = useChatsSnapshot();

  // page.navigated — the in-app twin of the server's page.viewed. The first
  // location is the server-rendered page (already counted as page.viewed), so
  // only changes after it fire; surface/path carry the destination page,
  // mirroring the server's surface mapping.
  //
  // `mode` and `tab` are part of the page identity: /talk → /talk?mode=chat
  // opens a different surface in place, and without them the chat page opened
  // from the chooser had no page-level record at all. Other query params
  // (returnTo, live search text synced by replaceUrl) are NOT — comparing the
  // whole search string would fire a navigation per keystroke.
  const mode = loc.search.get('mode') || '';
  const tab = loc.search.get('tab') || '';
  const navKey = `${loc.path}|${mode}|${tab}`;
  const prevNavKeyRef = useRef(navKey);
  // The route being LEFT — page.navigated's from_route. Held here rather than
  // read off history so each transition row is a complete fact on its own: a
  // lost sibling beacon can then never silently mint a false A→C hop.
  const prevRouteRef = useRef(loc.route.name);
  useBrowserLayoutEffect(() => {
    // Order matters, and it is the whole reason page identity is held rather
    // than read off window.location: endPageVisit() flushes how long the
    // PREVIOUS page held someone and how far down it they got, so it has to
    // run while that page is still the declared one. Then the new page is
    // declared, and page.navigated lands on it.
    if (navKey !== prevNavKeyRef.current) {
      prevNavKeyRef.current = navKey;
      const fromRoute = prevRouteRef.current;
      prevRouteRef.current = loc.route.name;
      endPageVisit();
      setTrackingPage({ surface: emberPageSurface(loc.path), route: loc.route.name, path: loc.path });
      track('page.navigated', {
        route: loc.route.name,
        from_route: fromRoute,
        method: lastNavigationMethod(),
        mode: mode || undefined,
        tab: tab || undefined,
      });
      return;
    }
    prevRouteRef.current = loc.route.name;
    setTrackingPage({ surface: emberPageSurface(loc.path), route: loc.route.name, path: loc.path });
  }, [navKey, loc.path, loc.route.name, mode, tab]);

  // The re-consent door for existing accounts.
  //
  // v1.2 deleted the inbound gate that used to send people to /legal/review and
  // put nothing in its place, so bumping CURRENT_LEGAL_VERSION prompted nobody.
  // This is the web-first replacement: /api/me reports whether the signed-in
  // account's acceptance is current, and the ember API refuses every write on
  // the same signal — so this redirect is the way OUT of a state the user is
  // already in, not the enforcement itself. A tampered client skips the screen
  // and still cannot tell Ember anything.
  //
  // A full-page assign, not a router navigate: /legal/review is a
  // server-rendered shell route with its own bootstrap, which the SPA router
  // cannot produce. `returnTo` carries them back to the page they were on.
  //
  // Waits for `authenticated` — during `loading` the flag is the first-paint
  // default (false), and acting on it either way would flash the wrong screen.
  useEffect(() => {
    if (sessionStatus !== 'authenticated') return;
    // A scheduled-for-deletion account is not asked to re-accept anything: the
    // deletion screen below is the only decision on the table, and sending
    // them to /legal/review first would put a re-consent door in front of
    // somebody who may well be leaving.
    if (session?.deletion_pending || session?.account_closed) return;
    if (!session?.legal_needs_review) return;
    const back = `${window.location.pathname}${window.location.search}`;
    window.location.assign(`/legal/review?returnTo=${encodeURIComponent(back)}`);
  }, [sessionStatus, session?.legal_needs_review, session?.deletion_pending, session?.account_closed]);

  // Legacy door shim: the old product's sign-in door was a dialog on the
  // homepage, addressed as `/?signin=1&next=<path>` (or `?from=join|dashboard|
  // trial`, which predate the flag and mean the same thing). Nothing mints
  // those addresses any more — every entrance points at /auth as of
  // 2026-08-31 — but they are live in shipped ad creative, in authored
  // content published before the change, and in texts already sent. So this
  // keeps translating them rather than stranding the visitor on the landing,
  // and carries `from` across as the entrance's copy (?arrival=…) so an old
  // trial-claim link still reads "Keep your story".
  useEffect(() => {
    if (loc.route.name !== 'landing') return;
    const from = (loc.search.get('from') || '').trim();
    const asked = loc.search.get('signin') === '1'
      || from === 'join' || from === 'dashboard' || from === 'trial';
    if (!asked) return;
    const fallback = from === 'dashboard' ? '/me' : '/collect';
    const next = sanitizeReturnTo(loc.search.get('next') || fallback, fallback);
    const arrival = from === 'trial' || from === 'dashboard' ? `&arrival=${from}` : '';
    navigate(`/auth?returnTo=${encodeURIComponent(next)}${arrival}`, { replace: true });
  }, [loc.route.name, loc.search]);

  // The deletion window's door, and the counterpart to the legal one above.
  //
  // Signing in stopped cancelling a pending deletion (2026-08-21), so an
  // authenticated session on a scheduled-for-deletion account is now a state
  // the app must render — and the only thing to render is the decision. Every
  // path, not a redirect to one: there is no product to be inside while the
  // account is on its way out, and the API refuses each write from the same
  // signal, so a browsable shell would just be a wall of 403s.
  //
  // Same `authenticated`-only guard as the legal door: during `loading` the
  // flag is the first-paint default (false), and a flash of this screen at
  // somebody with a healthy account would be its own small disaster.
  if (sessionStatus === 'authenticated' && session?.deletion_pending) {
    return (
      <div className="ember-app">
        <PendingDeletionScreen />
      </div>
    );
  }

  // An account Oriscen closed (account-stopped.js): the same door, with no way
  // back from the person's side. Also what an open app switches to the moment
  // a request is answered 403 account_closed (2026-10-21 Riley): the router
  // unmounts, and the chat, call or room on screen closes with it.
  if (sessionStatus === 'authenticated' && session?.account_closed) {
    return (
      <div className="ember-app">
        <AccountClosedScreen where={session.account_closed_where || 'app_open'} />
      </div>
    );
  }

  // The re-consent door (the effect above) is a full-page assign. While it is
  // on its way nothing is mounted beneath: a page that mounts here starts its
  // reads, the navigation aborts them before pagehide, and each catch records
  // a failure nobody met (prod 2026-09-16: three settings_load load_failed
  // rows off the reach_notice link, /legal/review rendered 150 ms later). The
  // same shape as the deletion screen — one decision on the table, so one
  // thing on the screen.
  if (sessionStatus === 'authenticated' && session?.legal_needs_review) {
    return <div className="ember-app" />;
  }

  return (
    // One sheet for the whole app: every in-app telling door opens it in place
    // instead of navigating to /talk (see talk-sheet.tsx).
    <ChatFrameProvider active={chatSurface} zone={chats?.clock.zone ?? ''} split={split}>
    <TalkSheetProvider>
      <div className={`ember-app${loc.route.name === 'landing' ? ' is-landing' : ''}${split ? ' has-chats-pane' : ''}`}>
        {hideTopNav ? null : (
          <TopNav
            showSearchField={loc.route.name === 'landing'}
            // The query by its text: URLSearchParams.size is undefined before iOS Safari 17 (release check 2026-10-27).
            currentPath={`${loc.path}${loc.search.toString() ? `?${loc.search.toString()}` : ''}${loc.hash}`}
          />
        )}
        {split && <ChatsPane here={chatHere} />}
        <main className="ember-main">
          <PageSwitch loc={loc} initialSsrData={initialSsrData} />
        </main>
        {/* A line landing in another conversation: a banner over any page but home, the list, a call and sign-in; the
            system's notification while the tab is away. */}
        {signedIn && <ChatBanner where={bannerWhere} />}
        {/* The registration text somebody is still expected to send. Mounted
            here, once, because the wait outlives the door that opened it: they
            leave for Messages from /auth or a Register CTA and come back to
            the same page, reloaded or not, and the sign-in has to complete
            anyway (2026-08-31). It does not outlive the PAGE (2026-09-03): the
            route path is handed down so a move to any other page ends the
            wait instead of trailing its pill around the site. Renders nothing
            when nothing is pending. */}
        <InboundWaitOverlay path={loc.path} />
        {/* The legal update notice (components/LegalUpdateNotice) is NOT
            mounted (2026-10-02 拍板, jam): continuing to use Ember is the
            agreement. Since 2026-10-15 an update is told by a text (notice
            legal_update) and by the horn on the first screen, both opening
            /legal/update (pages/LegalUpdate.tsx), where it can be answered. */}
      </div>
    </TalkSheetProvider>
    </ChatFrameProvider>
  );
}

function TopNav({ showSearchField, currentPath }: {
  showSearchField: boolean;
  currentPath: string;
}): ReactElement {
  const { status } = useSession();
  const authed = status === 'authenticated';

  return (
    <header className="ember-nav">
      <div className="ember-nav-inner">
        <Link to="/" className="ember-logo" aria-label="Ember — home" data-log="nav_logo">
          <EmberLockup className="ember-logo-lockup" />
          <span className="ember-logo-beta">beta</span>
        </Link>
        <nav className="ember-nav-actions" aria-label="Site">
          {/* /blog is served by the marketing document, not the SPA router. */}
          <BlogNav />
          <div className="ember-nav-controls">
            {/* v4.0 (2026-09-04): no Search — the product has no discovery
                surface (§0.3). Install keeps its slot. */}
            {showSearchField ? (
              <AddToHomeButton
                className="ember-nav-install"
                label="Install"
                ariaLabel="Add Ember to your Home Screen"
                source="home"
              />
            ) : null}
            {/* Exactly one account affordance, never both (2026-08-16):
                logged out → the Sign in pill; logged in → the profile
                icon (no unread badge since 2026-10-21: what it counted is behind
                the Chats and notebook doors, not in the menu). While the session
                is still loading, render neither — a wrong-state flash reads
                worse than a beat of blank. */}
            {status === 'loading' ? null : authed ? (
              <AccountMenu buttonClassName="ember-nav-icon ember-nav-profile">
                <ProfileGlyph />
              </AccountMenu>
            ) : (
              <Link
                to={`/auth?returnTo=${encodeURIComponent(currentPath)}`}
                className="ember-nav-cta"
                data-log="nav_register_login"
              >
                <svg className="ember-nav-signin-icon" viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M14 8l4 4-4 4M18 12H8" />
                  <path d="M11 5H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h5" />
                </svg>
                <span>Sign in</span>
              </Link>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}

function ProfileGlyph(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20c1.6-3.2 4.3-4.8 7.5-4.8s5.9 1.6 7.5 4.8" />
    </svg>
  );
}

/**
 * A route the product no longer has (v4.0 §0.3): replace onto `to` so the
 * retired URL never sits in history, and render nothing meanwhile.
 */
function RetiredRoute({ to }: { to: string }): ReactElement {
  useEffect(() => { navigate(to, { replace: true }); }, [to]);
  return <div className="ember-page-quiet" />;
}

function PageSwitch({ loc, initialSsrData }: {
  loc: RouteLocation;
  initialSsrData?: EmberSsrData | null;
}): ReactElement {
  const { route } = loc;
  const boot = readBootstrap();
  const bootSsr = boot.route === 'ember' && boot.ssr?.path === loc.path ? boot.ssr : null;
  const ssr = initialSsrData?.path === loc.path ? initialSsrData : bootSsr;
  switch (route.name) {
    case 'landing': {
      const initial = ssr?.page === 'home' ? ssr : null;
      return (
        <LandingPage
          initialHome={initial?.home ?? (boot.route === 'ember' ? boot.home : null)}
          initialLightId={loc.search.get('light')}
        />
      );
    }
    // Pages the product no longer has (v4.0 §0.3, 2026-09-04; /me/mattered
    // 2026-09-25; the v3.0 public Ember page 2026-09-30; the notebook's
    // Questions and Rooms tabs 2026-10-20): the server 301s the older ones,
    // and an in-app link lands here and is replaced onto its successor.
    case 'retired':
      return <RetiredRoute to={route.to} />;
    case 'talk':
      // The telling entrance: browser chat (?mode=chat), in-browser voice, or
      // a persistent iMessage thread. The chooser names the channel before it
      // asks the user to cross into it.
      // A way back from Ember games' quiz (2026-10-22): the link names the
      // animal and topic; the handoff writes the first line and opens the chat.
      if (loc.search.get('mode') === 'chat' && loc.search.get('quiz')) {
        return <QuizTalkHandoff key={loc.search.toString()} search={loc.search} />;
      }
      // The same from the games' yard (2026-10-23): the link names the door and the tag, never the words.
      if (loc.search.get('mode') === 'chat' && loc.search.get('yard')) {
        return <YardTalkHandoff key={loc.search.toString()} search={loc.search} />;
      }
      // The same from the games' Gas Me Up (2026-10-30): the link names the door and a card's type, never the words.
      if (loc.search.get('mode') === 'chat' && loc.search.get('gasmeup')) {
        return <GasMeUpTalkHandoff key={loc.search.toString()} search={loc.search} />;
      }
      // The same from a self-check page (/check/<slug>, 2026-10-27): the link names the check and the ticked answers as digits.
      if (loc.search.get('mode') === 'chat' && loc.search.get('check')) {
        return <CheckTalkHandoff key={loc.search.toString()} search={loc.search} />;
      }
      if (loc.search.get('mode') === 'chat') {
        return <TalkPage key={loc.search.toString()} search={loc.search} />;
      }
      return <TalkChooserPage search={loc.search} />;

    case 'auth':
      return <AuthPage search={loc.search} />;
    case 'result':
      return <ResultPage key={route.conversationId} conversationId={route.conversationId} tab={route.tab} hash={loc.hash} search={loc.search} />;
    case 'settings':
      return <SettingsPage />;
    case 'settings-space':
      return <div className="ember-page-quiet">Opening your profile…</div>;
    case 'feedback':
      return <FeedbackPage />;
    case 'age':
      return <AgeCheckPage />;
    case 'legal-update':
      return <LegalUpdatePage />;
    case 'me':
      return <div className="ember-page-quiet">Opening your profile…</div>;
    case 'me-embers':
      return <MeShell tab="embers" path={loc.path} />;
    case 'me-letters':
      return <MeShell tab="letters" path={loc.path} />;
    case 'me-letter-detail':
      return <LetterPage letterId={route.letterId} read={Boolean(route.read)} path={loc.path} />;
    case 'me-unlock':
      // The texted link's landing (§4.12): the ticket rides the query, so the
      // page re-mounts if a second link is opened over the first.
      return <NotebookUnlockPage key={loc.search.toString()} search={loc.search} />;
    case 'me-ember-detail':
      return <OwnerEmberDetailPage emberId={route.emberId} path={loc.path} />;
    case 'me-ember-transcript':
      return <EmberTranscriptPage emberId={route.emberId} path={loc.path} />;
    case 'person':
      return <PersonPage token={route.token} />;
    case 'handoff':
      return <HandoffPage key={route.token} token={route.token} initialData={ssr?.page === 'handoff' ? ssr.detail : null} />;
    case 'letter-handoff':
      return <LetterHandoffPage key={route.token} token={route.token} initialData={ssr?.page === 'letter_handoff' ? ssr.detail : null} />;
    case 'question':
      return <QuestionPage key={route.questionId} questionId={route.questionId} />;
    case 'read':
      return <ReadPage key={route.readId} readId={route.readId} path={loc.path} />;
    case 'how-it-went':
      return <HowItWentPage key={route.precedentId} precedentId={route.precedentId} />;
    case 'room':
      return <ChatRoomFrame><RoomPage key={route.roomId} roomId={route.roomId} /></ChatRoomFrame>;
    case 'chats':
      return <ChatsPage />;
    case 'friends':
      return <FriendsPage view={loc.search.get('view')} />;
    case 'friends-new':
      return <FriendSetupPage search={loc.search} />;
    case 'friend-intro':
      return <FriendIntroPage key={route.roomId} roomId={route.roomId} />;
    case 'friend-checkin':
      return <FriendCheckinPage key={route.roomId} roomId={route.roomId} />;
    case 'topic-new':
      return <TopicNewPage search={loc.search} />;
    case 'topic-room':
      return <TopicRoomPage key={route.roomId} roomId={route.roomId} invited={loc.search.get('invited') === '1'} />;
    case 'groups':
      return <GroupsPage />;
    case 'group':
      return <GroupPage key={route.groupId} groupId={route.groupId} />;
    case 'group-new':
      return <GroupNewPage key={`${route.groupId}?${loc.search.toString()}`} groupId={route.groupId} search={loc.search} />;
    case 'group-post':
      return <GroupPostPage key={route.postId} postId={route.postId} answering={loc.search.get('answer') === '1'} />;
    case 'place':
      return <PlacePage slug={route.slug} initialData={ssr?.page === 'place' ? ssr.place : null} />;
    case 'not-found':
    default:
      return <NotFound />;
  }
}

function NotFound(): ReactElement {
  return <PageFailure title="This page doesn't exist" detail="The link may be old, or the address mistyped." logState="spa_404" face="curious" />;
}
